import { useCallback, useState } from 'react';
import { useIntl, IntlShape } from 'react-intl';
import { EVENT_NAME, ROLES, Tabs } from 'common/constants';
import { ErrorToastValidation } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { getErrorMessages } from 'components/ErrorMessageHandler/slice';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { useFlags } from 'hooks';
import {
  usePostPaymentApprovals,
  useSubmitAuthorizedApCheck,
  useUpdateInvoiceDescriptions,
  useUpdateInvoiceLockStatus,
  useUpdateInvoiceState
} from 'hooks/useClientDataServices';
import {
  getSelectedTab,
  setSelectedTab
} from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getUserRole } from 'pages/LandingPage/slice';
import { updateActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/slice';
import {
  clearSelectedInvoices,
  getAuthorizeApChecksPayload,
  getLockInvoicesPayload,
  getPostPaymentApprovalsPayload,
  getSelectedTotal,
  getSuccessfullyLockedInvoicesLength,
  setDueDate,
  setSuccessfullyLockedInvoices
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { store } from 'store';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { formatCurrency } from 'utils/formatCurrency';
import { InvoiceTable } from './components';
import { AuthorizePaymentHeader } from './components/AuthorizePaymentHeader/AuthorizePaymentHeader';
import { messages, errors } from './messages';
import { getUpdateInvoiceDescriptionsPayload } from './slice';
import {
  StyledTotal,
  StyledSpan,
  StyledDiv,
  StyledTotalAmount
} from './styled';
import { LockStatus } from './types';

export const AuthorizePaymentCheck = (): React.JSX.Element => {
  const amplitude = useAmplitude();
  const intl: IntlShape = useIntl();
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const selectedTotal = useAppSelector(getSelectedTotal);
  const lockInvoicesPayload = useAppSelector(getLockInvoicesPayload);
  const apChecksPayload = useAppSelector(getAuthorizeApChecksPayload);
  const selectedTab = useAppSelector(getSelectedTab);
  const numInvoices = useAppSelector(getSuccessfullyLockedInvoicesLength);
  const errorMessages: string[] = useAppSelector(getErrorMessages);
  const updateInvoiceDescriptionsPayload = useAppSelector(
    getUpdateInvoiceDescriptionsPayload
  );
  const [errorToast, setErrorToast] = useState(false);

  const redirect = useCallback(() => {
    dispatch(clearSelectedInvoices());
    dispatch(updateActiveAuthorizePaymentPage(''));
    if (selectedTab === Tabs.ToBePaid) {
      dispatch(
        setSuccessMessage(
          intl.formatMessage(
            numInvoices > 1 ? messages.invoicesMoved : messages.invoiceMoved
          )
        )
      );
    } else if (selectedTab === Tabs.NeedsAuthorization) {
      const successToastMessage =
        apChecksPayload?.length > 1
          ? messages.authorizeChecksApproval
          : messages.authorizeCheckApproval;
      dispatch(setSuccessMessage(intl.formatMessage(successToastMessage)));
    }
    const newTab =
      selectedTab === Tabs.ToBePaid
        ? Tabs.NeedsAuthorization
        : Tabs.PrintChecks;
    dispatch(setSelectedTab(newTab));
    dispatch(setDueDate(''));
  }, [dispatch, numInvoices, selectedTab, intl, apChecksPayload?.length]);

  const { mutate: updateInvoiceState, isLoading: isUpdateInvoiceStateLoading } =
    useUpdateInvoiceState();

  const {
    mutate: updateInvoiceDescriptions,
    isLoading: isUpdateInvoiceDescriptionsLoading
  } = useUpdateInvoiceDescriptions();

  const { mutate: postPaymentApprovals, isLoading: isPostApprovalsLoading } =
    usePostPaymentApprovals();

  const { mutate: updateLockStatus, isLoading: isLockInvoicesLoading } =
    useUpdateInvoiceLockStatus();

  const { mutate: authorizeApCheck, isLoading: isAuthorizeApChecksLoading } =
    useSubmitAuthorizedApCheck(true);

  const userRole = useAppSelector(getUserRole);

  const handleAuthorizePayment = () => {
    if (isUpdateInvoiceStateLoading || isLockInvoicesLoading) return;

    if (updateInvoiceDescriptionsPayload.length > 0) {
      updateInvoiceDescriptions(updateInvoiceDescriptionsPayload);
    }
    if (amplitude && selectedTab === Tabs.ToBePaid) {
      amplitudeTracker(amplitude, EVENT_NAME.RequestAuthorization);
    }
    if (
      selectedTab === Tabs.NeedsAuthorization &&
      flags.dmsAccountingThreeStepApproval &&
      flags.dmsAccountingLandingPage
    ) {
      amplitudeTracker(amplitude, EVENT_NAME.AuthorizePayment, {
        'Vendor Total': apChecksPayload.length
      });
      const vendorInvoicePairs = apChecksPayload.map(
        ({ vendorNo, invoiceId }) => ({
          vendorNo,
          invoiceId
        })
      );
      updateInvoiceState(
        {
          state: 'AUTHORIZED',
          payload: { vendorInvoicePairs }
        },
        {
          onError: () => {
            setErrorToast(true);
            updateLockStatus({
              lockStatus: LockStatus.UNLOCKED,
              invoices: lockInvoicesPayload
            });
          },
          onSuccess: redirect
        }
      );
    } else if (
      selectedTab === Tabs.NeedsAuthorization &&
      flags.dmsAccountingThreeStepApproval &&
      !flags.dmsAccountingLandingPage
    ) {
      amplitudeTracker(amplitude, EVENT_NAME.AuthorizePayment, {
        'Vendor Total': apChecksPayload.length
      });
      authorizeApCheck(apChecksPayload, {
        onSuccess: redirect
      });
    } else {
      updateLockStatus(
        { lockStatus: LockStatus.LOCKED, invoices: lockInvoicesPayload },
        {
          onSuccess: ({ failures, successes }) => {
            if (successes.length === 0) return;
            dispatch(setSuccessfullyLockedInvoices(successes));
            dispatch(clearSelectedInvoices(successes));
            if (
              flags.dmsAccountingLandingPage &&
              selectedTab === Tabs.ToBePaid
            ) {
              const vendorInvoicePairs = successes.map(
                ({ vendorNo, invoiceId }) => ({
                  vendorNo,
                  invoiceId
                })
              );
              const invoiceStatePayload = { vendorInvoicePairs };
              updateInvoiceState(
                {
                  state: 'APPROVED',
                  payload: invoiceStatePayload
                },
                {
                  onError: () => {
                    setErrorToast(true);
                    updateLockStatus({
                      lockStatus: LockStatus.UNLOCKED,
                      invoices: lockInvoicesPayload
                    });
                  },
                  onSuccess: () => {
                    if (failures.length === 0) {
                      redirect();
                    }
                  }
                }
              );
            } else {
              const paymentApprovalsPayload = getPostPaymentApprovalsPayload(
                store.getState()
              );
              postPaymentApprovals(paymentApprovalsPayload, {
                onError: () => {
                  setErrorToast(true);
                  updateLockStatus({
                    lockStatus: LockStatus.UNLOCKED,
                    invoices: lockInvoicesPayload
                  });
                },
                onSuccess: () => {
                  if (failures.length === 0) {
                    redirect();
                  }
                }
              });
            }
          }
        }
      );
    }
  };

  return (
    <>
      <AuthorizePaymentHeader
        handleAuthorizePayment={handleAuthorizePayment}
        disableAuthButton={
          userRole === ROLES.AP_CLERK && selectedTab !== Tabs.ToBePaid
        }
        isLoading={
          isLockInvoicesLoading ||
          isUpdateInvoiceDescriptionsLoading ||
          isUpdateInvoiceStateLoading ||
          isPostApprovalsLoading ||
          isAuthorizeApChecksLoading
        }
      />
      <StyledDiv>
        {(errorToast && errorMessages[0]) ?? (
          <ErrorToastValidation
            errorMessage={intl.formatMessage(errors.postApproval)}
            handleDismiss={() => setErrorToast(false)}
            handleRetry={() => {
              handleAuthorizePayment();
              setErrorToast(false);
            }}
            onlyOneButtonProps={false}
          />
        )}
        <StyledTotalAmount>
          <StyledTotal>{intl.formatMessage(messages.total)}</StyledTotal>
          <StyledSpan>{formatCurrency(selectedTotal)}</StyledSpan>
        </StyledTotalAmount>
      </StyledDiv>
      <InvoiceTable />
    </>
  );
};
